import type { TagTone } from '../../_detailShared/detail.styled';

/**
 * Unified-contribution status vocabulary (spec 0004 §3.2). The backend is
 * the source of truth — `ContributionType.status` arrives as a lowercase
 * String (not an enum), derived from the linked Fakturownia invoices and
 * the hidden Payment records. The old client-side
 * complete/remainder/delay arithmetic survives only as a fallback for
 * pre-0004 backends.
 */
export type ContributionStatus =
  | 'future'
  | 'awaiting'
  | 'invoiced'
  | 'overdue'
  | 'partial'
  | 'paid'
  | 'paid_manual';

const ALL_STATUSES: ContributionStatus[] = [
  'future',
  'awaiting',
  'invoiced',
  'overdue',
  'partial',
  'paid',
  'paid_manual',
];

/** Chip palette per spec 0004: future=default, awaiting=orange,
 *  invoiced=blue, overdue=red, partial=gold, paid/paid_manual=green. */
export const CONTRIBUTION_STATUS_TONE: Record<ContributionStatus, TagTone> = {
  future: 'neutral',
  awaiting: 'warn',
  invoiced: 'info',
  overdue: 'danger',
  partial: 'gold',
  paid: 'ok',
  paid_manual: 'ok',
};

interface IContributionStatusSource {
  status?: string | null;
  complete?: boolean | null;
  remainder?: number | null;
  delay?: number | null;
}

const isKnownStatus = (value: string): value is ContributionStatus =>
  (ALL_STATUSES as string[]).includes(value);

/**
 * Backend `status` is a plain String — compare lowercase literals (choice
 * strings are lowercase on the wire). Falls back to the legacy client-side
 * derivation when the field is missing (old backend / partial cache).
 */
export const contributionStatusOf = (
  c: IContributionStatusSource,
): ContributionStatus => {
  const backend = (c.status || '').toLowerCase();
  if (isKnownStatus(backend)) return backend;
  if (c.complete) return (c.remainder ?? 0) > 0 ? 'partial' : 'paid';
  return (c.delay ?? 0) > 0 ? 'overdue' : 'future';
};

/** paid | paid_manual — the states corrections can target. */
export const isPaidContributionStatus = (status: ContributionStatus): boolean =>
  status === 'paid' || status === 'paid_manual';
